<script lang="ts" setup>
	import StringMethods from '~/utils/character'

	export type HeadStatistics = {
		[id: string]: {
			value: string | number
			header_class?: string
			paragraph_class?: string
			wrapper_class?: string
			disabled?: boolean
		}
	}

	defineComponent({
		name: 'SharedHeadStats'
	})

	interface Paragraph {
		size: string
		font: string
		margin: string
		default: string
	}
	interface Title {
		default: string
		size: string
		font: string
		margin: string
	}
	type StatItem = {
		default: string
		border: string
		color: string
		width: string
		paddingInline: string
		paddingBlock: string
		title: Partial<Title>
		paragraph: Partial<Paragraph>
	}
	interface Wrapper {
		default: string
		display: string
		rounded: string
		margin: string
		paddingBlock: string
		paddingInline: string
		gap: string
		height: string
		alignItems: string
		border: string
	}
	type StatConfig = {
		wrapper: Partial<Wrapper>
		statItem: Partial<StatItem>
	}

	defineProps<{
		statsDetails: HeadStatistics
		contentWrapper?: string
		wrapperClass?: ClassContext
		ui?: Partial<StatConfig>
	}>()

	const { statItem, wrapper }: StatConfig = {
		wrapper: {
			default: '',
			display: 'flex-1 inline-flex flex-wrap',
			rounded: 'rounded-lg',
			margin: 'mx-auto',
			paddingBlock: 'py-5 ',
			paddingInline: 'px-1',
			gap: 'sm:gap-0 gap-x-3 gap-y-4 ',
			alignItems: 'items-center',
			border: 'border',
			height: 'min-h-[82px]'
		},
		statItem: {
			default: '',
			border: 'last:border-0',
			color: 'text-gray-600',
			width: 'min-w-[200px]',
			paddingInline: 'px-3.5',
			paddingBlock: '',
			title: {
				default: 'capitalize',
				size: 'text-sm ',
				font: 'font-medium',
				margin: 'mb-2'
			},
			paragraph: {
				size: 'text-[24px]',
				font: 'font-semibold ',
				margin: 'my-0'
			}
		}
	}
	const { stripUnderScore } = StringMethods()
</script>

<template>
	<div
		:class="[
			wrapperClass,
			ui?.wrapper?.alignItems ?? wrapper.alignItems,
			ui?.wrapper?.border ?? wrapper.border,
			ui?.wrapper?.default ?? wrapper.default,
			ui?.wrapper?.display ?? wrapper.display,
			ui?.wrapper?.gap ?? wrapper.gap,
			ui?.wrapper?.margin ?? wrapper.margin,
			ui?.wrapper?.paddingBlock ?? wrapper.paddingBlock,
			ui?.wrapper?.paddingInline ?? wrapper.paddingInline,
			ui?.wrapper?.rounded ?? wrapper.rounded
		]"
	>
		<div
			v-for="(val, k, index) in statsDetails"
			:key="index"
			:class="[
				val.wrapper_class,
				contentWrapper,
				{ hidden: val?.disabled ?? false, 'sm:border-x': index % 2 },
				ui?.statItem?.border ?? statItem.border,
				ui?.statItem?.color ?? statItem.color,
				ui?.statItem?.default ?? statItem.default,
				ui?.statItem?.paddingBlock ?? statItem.paddingBlock,
				ui?.statItem?.paddingInline ?? statItem.paddingInline,
				ui?.statItem?.width ?? statItem.width
			]"
		>
			<span
				:class="[
					val.header_class,
					ui?.statItem?.title?.default ?? statItem.title?.default,
					ui?.statItem?.title?.font ?? statItem.title?.font,
					ui?.statItem?.title?.margin ?? statItem.title?.margin,
					ui?.statItem?.title?.size ?? statItem.title?.size
				]"
			>
				<slot name="title">
					{{ stripUnderScore(k as string) }}
				</slot>
			</span>
			<h5
				:class="[
					val.paragraph_class,
					ui?.statItem?.paragraph?.font ?? statItem.paragraph?.font,
					ui?.statItem?.paragraph?.margin ?? statItem.paragraph?.margin,
					ui?.statItem?.paragraph?.size ?? statItem.paragraph?.size,
					ui?.statItem?.paragraph?.default ?? statItem.paragraph?.default
				]"
			>
				<slot name="paragraph">
					{{ val.value }}
				</slot>
			</h5>
		</div>
	</div>
</template>
